import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  Avatar,
  avatarType,
  btnType,
  Button,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  Icons,
  Text,
} from '../../../../packages';
import { Menu } from '../../../../packages/ui/molecules/menu';
import { ConfirmationModal } from '../../../../packages/ui/templates/confirmation-modal';
import dayjs, { getUserTimeZone } from '../../../../utils/dayjs';
import history from '../../../../utils/history';
import { useStyles } from './WaitingRoomTopBar.styles';
import { appointmentActionCreators } from '../../../../redux/modules/appointment';
import { getProfile } from '../../../../redux/modules/profile/selectors';
import { getAuth } from '../../../../redux/modules/auth/selectors';
import AppointmentScheduling from '../../appointments/components/appointment-scheduling';
import AppointmentCancel from '../../appointments/components/appointment-cancel';

const WaitingRoomTopBar = ({
  appointment,
  onEndSession,
  startInPersonSession,
  showCallCompletedByPhone,
  showInPersonSession,
  showMarkAsNoShow = false,
  showAMAInline = false,
  onCloseAMA,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { demographicDetails } = useSelector(getProfile);
  const { meta } = useSelector(getAuth);
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);

  const handleMarkAsNoShow = () => {
    setShowNoShowModal(true);
  };

  const editAppointmentClick = () => {
    setShowReschedule(true);
  };

  const cancelAppointmentClick = () => {
    setShowCancel(true);
  };

  const onCloseSlider = () => {
    showReschedule && setShowReschedule(false);
    showCancel && setShowCancel(false);
    showNoShowModal && setShowNoShowModal(false);
  };

  const updateAppointment = payload => {
    history.push(
      `/provider/${meta?.userId}/day-schedule?date=${dayjs().tz(getUserTimeZone()).format('YYYY-MM-DD')}`,
    );
  };

  const handleConfirmMarkNoShow = () => {
    dispatch(
      appointmentActionCreators.cancelAppointment({
        appointmentId: appointment.appointmentId,
        appointmentCancelParams: { reason: 'NO_SHOW' },
        callback: errorApi => {
          if (errorApi) {
            console.log(errorApi);
          } else {
            history.push(
              `/provider/${meta?.userId}/day-schedule?date=${dayjs()
                .tz(getUserTimeZone())
                .format('YYYY-MM-DD')}`,
            );
          }
        },
      }),
    );
    setShowNoShowModal(false);
  };

  const handleCancelMarkNoShow = () => {
    setShowNoShowModal(false);
  };

  const completeByPhoneClick = () => {
    onEndSession?.('PHONE');
  };

  const startInPersonCall = () => {
    startInPersonSession?.();
  };

  const handleBackClick = () => {
    if (showAMAInline && onCloseAMA) {
      onCloseAMA();
    } else {
      setShowBackConfirmation(true);
    }
  };

  const handleConfirmBack = () => {
    setShowBackConfirmation(false);
    history.back();
  };

  const handleCancelBack = () => {
    setShowBackConfirmation(false);
  };

  // Build menu items conditionally
  const menuItems = [];

  if (showCallCompletedByPhone) {
    menuItems.push({
      label: 'Call completed by phone',
      onClick: completeByPhoneClick,
    });
  }

  if (showInPersonSession) {
    menuItems.push({
      label: 'Start in person session',
      onClick: startInPersonCall,
    });
  }
  menuItems.push({
    label: 'Reschedule Appointment',
    onClick: editAppointmentClick,
  });
  if (!showMarkAsNoShow) {
    menuItems.push({
      label: 'Mark as no show',
      onClick: handleMarkAsNoShow,
    });
  }
  menuItems.push({
    label: 'Cancel Appointment',
    onClick: cancelAppointmentClick,
  });

  return (
    <Box className={classes.container}>
      <Box display="flex" alignItems="center">
        <Button variant={btnType.OUTLINE} className={classes.btnBack} onClick={handleBackClick}>
          <Icons color={colors.primary500} glyph="arrow-left" />
        </Button>
        <Avatar
          src={appointment?.participantImage}
          name={appointment?.participantName}
          size={40}
          variant={avatarType.CIRCLE}
        />
        <Box className={classes.info}>
          <Heading level={headingLevel.M} weight={fontWeight.SEMI_BOLD} className={classes.title}>
            {appointment?.patientFirstName || ''} {appointment?.patientLastName || ''} •{' '}
            {demographicDetails?.uuid || ''}
          </Heading>
          <Text weight={fontWeight.MEDIUM} className={classes.subtitle}>
            {appointment?.serviceDuration} Min {appointment?.serviceName}
          </Text>
        </Box>
      </Box>
      <Box display="flex" alignItems="center">
        {showMarkAsNoShow && (
          <Button variant={btnType.SECONDARY} className={classes.btnMarkNoShow} onClick={handleMarkAsNoShow}>
            Mark as no show
          </Button>
        )}
        {menuItems.length > 0 && (
          <Menu
            icon="more-vertical"
            className={classes.menu}
            itemsWrapperClassName={classes.menuItemsWrapper}
            items={menuItems}
          />
        )}
      </Box>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={showNoShowModal}
        onClose={handleCancelMarkNoShow}
        onSave={handleConfirmMarkNoShow}
        title="Mark as no show?"
        message={`Are you sure you want to mark ${
          appointment?.patientFirstName || 'this member'
        } as no show? This action cannot be undone.`}
        noLabel="Cancel"
        yesLabel="Mark as no show"
      />
      <ConfirmationModal
        open={showBackConfirmation}
        onClose={handleCancelBack}
        onSave={handleConfirmBack}
        title="Leave waiting room?"
        message="Are you sure you want to leave the waiting room? You can return at any time."
        noLabel="Cancel"
        yesLabel="Leave"
      />
      <AppointmentScheduling
        hideCancelBtn
        open={showReschedule}
        onClose={onCloseSlider}
        updateAppointment={updateAppointment}
        appointment={appointment}
      />
      <AppointmentCancel
        open={showCancel}
        onClose={onCloseSlider}
        appointment={appointment}
        updateAppointment={updateAppointment}
      />
    </Box>
  );
};

export { WaitingRoomTopBar };
